import React from 'react';
import { Button } from 'antd';
import { customTheme } from './inspectorTheme';
import styles from './WidgetPortInspector.module.css';
import { TranslateFunction } from '@common/hooks/useTranslation';
import { isBase64String } from '@kemu-io/kemu-core/common/lib/utils/typeCheckUtils.js';

// Constants for array pagination
export const LARGE_ARRAY_THRESHOLD = 50; // Arrays with more than this many items will be virtualized
export const INITIAL_ARRAY_ITEMS = 50; // Number of items to show initially
export const LOAD_MORE_MARKER_PREFIX = '__LOAD_MORE__';

/**
 * Parses a load more marker string to extract path and remaining count.
 * Format: __LOAD_MORE__{path}__{remaining}
 * For root arrays: __LOAD_MORE____{remaining} (empty path between double underscores)
 *
 * @param marker - The marker string to parse
 * @returns Object with path and remaining count, or null if invalid
 */
export const parseLoadMoreMarker = (marker: string): { path: string; remaining: number } | null => {
  const match = marker.match(/^__LOAD_MORE__(.*?)__(\d+)$/);
  if (match) {
    const [, path, remainingStr] = match;
    return { path: path || '', remaining: parseInt(remainingStr, 10) };
  }
  return null;
};

/**
 * Renders an array count label with optional name (index).
 *
 * @param visibleCount - Number of visible items
 * @param totalCount - Total number of items (optional)
 * @param name - Optional name/index to display before the count
 * @returns React element displaying the array count
 */
export const renderArrayCount = (visibleCount: number, totalCount?: number, name?: string): React.ReactElement => {
  const countText = totalCount !== undefined ? `${visibleCount} of ${totalCount}` : visibleCount;
  const arrayText = `Array (${countText})`;

  if (name !== undefined) {
    return (
      <React.Fragment>
        <span style={{ color: customTheme.OBJECT_NAME_COLOR }}>{name}</span>
        <span style={{ color: customTheme.BASE_COLOR }}>: </span>
        <span style={{ color: customTheme.BASE_COLOR }}>{arrayText}</span>
      </React.Fragment>
    );
  }

  return (
    <span style={{ color: customTheme.BASE_COLOR }}>
      {arrayText}
    </span>
  );
};

/**
 * Renders a "Load more" button for paginated arrays.
 *
 * @param remaining - Number of remaining items to load
 * @param onClick - Callback function when button is clicked
 * @param wpT - Translation function
 * @returns React element displaying the load more button
 */
export const renderLoadMoreButton = (
  remaining: number,
  onClick: () => void,
  wpT: TranslateFunction
): React.ReactElement => (
  <div className={styles.LoadMoreInline}>
    <Button
      type="link"
      onClick={onClick}
      className={styles.LoadMoreButton}
      size="small"
    >
      {wpT('LoadMore', 'Load more ({remaining} remaining)', { remaining })}
    </Button>
  </div>
);

/**
 * Extracts metadata (visible count and total count) from a paginated array.
 * Searches for the load more marker to determine pagination state.
 *
 * @param array - The paginated array to analyze
 * @param nestedArrayVisibleCounts - Map of array paths to their visible counts
 * @returns Object with visibleCount and totalCount, or null if not paginated
 */
export const getArrayMetadataFromPaginatedArray = (
  array: unknown[],
  nestedArrayVisibleCounts: Map<string, number>
): { visibleCount: number; totalCount: number } | null => {
  // Search from end for better performance (load more marker is typically at the end)
  for (let i = array.length - 1; i >= 0; i--) {
    const item = array[i];
    if (typeof item === 'string' && item.startsWith(LOAD_MORE_MARKER_PREFIX)) {
      const parsed = parseLoadMoreMarker(item);
      if (parsed) {
        const visibleCount = nestedArrayVisibleCounts.get(parsed.path) ?? INITIAL_ARRAY_ITEMS;
        return { visibleCount, totalCount: parsed.remaining + visibleCount };
      }
      break; // Found marker, no need to continue
    }
  }
  return null;
};

/**
 * Recursively processes data to paginate nested large arrays.
 * Large arrays are sliced to show only visible items, with a load more marker if needed.
 *
 * @param data - The data to process (can be any type)
 * @param path - Current path in the data structure (used for tracking nested arrays)
 * @param nestedArrayVisibleCounts - Map of array paths to their visible counts
 * @returns Processed data with paginated arrays
 */
export const processNestedArrays = (
  data: unknown,
  nestedArrayVisibleCounts: Map<string, number>,
  path: string = ''
): unknown => {
  if (Array.isArray(data)) {
    const arrayLength = data.length;
    const isLargeArray = arrayLength > LARGE_ARRAY_THRESHOLD;

    if (isLargeArray) {
      const visibleCount = nestedArrayVisibleCounts.get(path) ?? INITIAL_ARRAY_ITEMS;
      const visibleItems = data.slice(0, visibleCount).map((item, index) =>
        processNestedArrays(item, nestedArrayVisibleCounts, `${path}[${index}]`)
      );
      return visibleCount < arrayLength
        ? [...visibleItems, `${LOAD_MORE_MARKER_PREFIX}${path}__${arrayLength - visibleCount}`]
        : visibleItems;
    }

    // Small array - recursively process items
    return data.map((item, index) => processNestedArrays(item, nestedArrayVisibleCounts, `${path}[${index}]`));
  }

  if (typeof data === 'object' && data !== null) {
    const result: Record<string, unknown> = {};
    for (const key in data as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const newPath = path ? `${path}.${key}` : key;
        result[key] = processNestedArrays((data as Record<string, unknown>)[key], nestedArrayVisibleCounts, newPath);
      }
    }
    return result;
  }

  return data;
};

/**
 * Recursively traverses a data structure and replaces Base64 encoded strings
 * with a placeholder string for display purposes.
 * 
 * Handles nested objects, arrays, and prevents infinite recursion through
 * depth limiting and circular reference detection.
 * 
 * @param value - The value to process (can be any type)
 * @param depth - Current recursion depth (used internally)
 * @param visited - Set of visited objects to prevent circular references (used internally)
 * @returns A new data structure with Base64 strings replaced by "<Base64 Encoded String>"
 * 
 * @example
 * ```typescript
 * const data = {
 *   name: "John",
 *   image: "aGVsbG8=", // Base64 string
 *   nested: {
 *     data: "SGVsbG8gV29ybGQ=" // Another Base64 string
 *   }
 * };
 * 
 * recursivelyReplaceBase64(data);
 * // Returns: {
 * //   name: "John",
 * //   image: "<Base64 Encoded String>",
 * //   nested: {
 * //     data: "<Base64 Encoded String>"
 * //   }
 * // }
 * ```
 */
export const recursivelyReplaceBase64 = (
  value: unknown,
  depth = 0,
  visited = new WeakSet()
): unknown => {
  // Prevent infinite recursion by limiting depth
  const MAX_DEPTH = 50;
  if (depth > MAX_DEPTH) {
    return '<Max Depth Exceeded>';
  }

  if (typeof value === 'string') {
    return isBase64String(value) ? '<Base64 Encoded String>' : value;
  }

  if (Array.isArray(value)) {
    return value.map((item) =>
      recursivelyReplaceBase64(item, depth + 1, visited)
    );
  }

  if (typeof value === 'object' && value !== null) {
    // Prevent circular references
    if (visited.has(value)) {
      return '<Circular Reference>';
    }
    visited.add(value);

    const newObj: Record<string, unknown> = {};
    for (const key in value as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        newObj[key] = recursivelyReplaceBase64(
          (value as Record<string, unknown>)[key],
          depth + 1,
          visited
        );
      }
    }
    return newObj;
  }

  return value;
};
